import { FC, ReactNode, useEffect, useState } from 'react'
import { AButton } from 'views/components/Analytics'
import { UsContract, UsContractStatuses } from 'modules/domain/usContracts/types'
import { AntSwitch, defaultTheme, Icons, Text, Typography } from '@agro-club/agroclub-shared'
import { useTranslation } from 'react-i18next'
import { useAProgress } from 'hooks/useAProgress'
import { apiClient } from 'modules/utils/httpClient'
import { endpoints } from 'modules/endpoints'
import { Card } from 'views/components/Card/Card'
import { ASharedModal, SharedModalContent, SharedModalTitle } from 'views/components/SharedModal'
import ADefaultModalFooter from 'views/components/DefaultModalFooter/ADefaultModalFooter'
import styled from 'styled-components'
import { usePermissions } from 'views/components/PermissionCheck/usePermissions'
import { SignError, SignWarnings } from './SignMessages'
import { RequestError } from 'modules/errors'

type Props = {
  contract: UsContract
  refetch: (affected?: UsContractStatuses) => Promise<void>
}

const SignedText = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;

  ${Typography.buttonLarge}

  svg {
    width: 20px;
    height: 20px;

    path {
      fill: ${defaultTheme.color.accentApprove600};
    }
  }
`

const Limit = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const LimitSwitches = styled.div`
  display: flex;
  gap: 24px;
  align-items: center;
`

export const SignBlock: FC<Props> = ({ contract, refetch }) => {
  const { t } = useTranslation(['usContracts'])
  const [confirmModal, setConfirmModal] = useState(false)
  const [justSigned, setJustSigned] = useState(false)
  const [signError, setSignError] = useState<ReturnType<typeof RequestError.parseError> | null>(null)
  const allowCLExceed = usePermissions(['contracts.credit_limit_overdraft_approval'])
  const allowPLExceed = usePermissions(['contracts.position_limit_overdraft_approval'])
  const hasPermToSign = usePermissions(['contracts.can_sign_contract'])
  const [internalCL, setInternalCL] = useState(contract.approved_credit_limit_overdraft)
  const [internalPL, setinternalPL] = useState(contract.approved_position_limit_overdraft)

  // sync if extrnal data changed
  useEffect(() => {
    setInternalCL(contract.approved_credit_limit_overdraft)
  }, [contract.approved_credit_limit_overdraft])

  useEffect(() => {
    setinternalPL(contract.approved_position_limit_overdraft)
  }, [contract.approved_position_limit_overdraft])

  const [signProgress, sign] = useAProgress(
    () => {
      setSignError(null)
      return apiClient.post(endpoints.signUsContract(contract.id))
    },
    {
      eventName: 'closeContract',
      onSuccess: async () => {
        await refetch()
        setConfirmModal(false)
        setJustSigned(true)
      },
      onError: (error) => {
        setSignError(error)
        setConfirmModal(false)
      },
    },
  )
  const [, changeCLExceed] = useAProgress(
    () => {
      setInternalCL(!internalCL)
      return apiClient.post(endpoints.usContractAllowCreditLimitExceed(contract.id))
    },
    {
      eventName: 'allowCLExceed',
      analyticsPayload: { id: contract.id.toString() },
      onSuccess: async () => {
        await refetch(contract.status)
      },
      onError: () => {
        setInternalCL(!internalCL) // revert the change if error occurs
      },
    },
  )
  const [, changePLExceed] = useAProgress(
    () => {
      setinternalPL(!internalPL)
      return apiClient.post(endpoints.usContractAllowPositionLimitExceed(contract.id))
    },
    {
      eventName: 'allowPLExceed',
      analyticsPayload: { id: contract.id.toString() },
      onSuccess: async () => {
        await refetch(contract.status)
      },
      onError: () => {
        setinternalPL(!internalPL) // revert the change if error occurs
      },
    },
  )
  if (contract.cancelled_at || (contract.status !== UsContractStatuses.new && !justSigned)) {
    return null
  }

  let jsx: ReactNode
  if (justSigned) {
    jsx = (
      <Text color="approve">
        <div style={{ display: 'flex', justifyContent: 'space-evenly' }}>
          <SignedText>
            <Icons.IconDone fill={defaultTheme.color.accentApprove600} />
            {t('contractSigned')}
          </SignedText>
        </div>
      </Text>
    )
  } else if (contract.status === UsContractStatuses.new) {
    jsx = (
      <Card.GapableContent>
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'space-between', alignItems: 'flex-end' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <Text color="secondary" typography="bodyMedium">
              {t('allowExceedLimits')}
            </Text>
            <LimitSwitches>
              <Limit>
                <AntSwitch disabled={!allowCLExceed} checked={internalCL} onChange={changeCLExceed} />
                <Text> {t('creditLimit')} </Text>
              </Limit>
              <Limit>
                <AntSwitch disabled={!allowPLExceed} checked={internalPL} onChange={changePLExceed} />
                <Text> {t('positionLimit')} </Text>
              </Limit>
            </LimitSwitches>
          </div>
          <AButton
            disabled={!hasPermToSign}
            color="secondary"
            onClick={() => setConfirmModal(true)}
            id="signContract"
            Icon={Icons.IconDone}
          >
            {t('sign')}
          </AButton>
        </div>
        <SignWarnings contract={contract} />
        <SignError error={signError} />
      </Card.GapableContent>
    )
  }

  return (
    <Card.Container padding="compact" bordered style={{ marginBottom: '4px' }}>
      {jsx}
      {confirmModal && (
        <ASharedModal id="singContractConfirm" onClose={() => setConfirmModal(false)} size="small">
          <SharedModalTitle>{t('signConfirm.title')}</SharedModalTitle>
          <SharedModalContent>{t('signConfirm.message')}</SharedModalContent>
          <ADefaultModalFooter onClose={() => setConfirmModal(false)} onConfirm={sign} progress={signProgress} />
        </ASharedModal>
      )}
    </Card.Container>
  )
}
